import { useState, type ReactNode } from "react";
import { formatBusinessType, formatIndustry, type Client } from "../types/client";
import { INDUSTRY_GROUPS } from "../data/industryGroups";
import { ENTITY_TYPES } from "../data/entityTypes";
import { ErrorMessage } from "./ErrorMessage";
import { AnimatedButton } from "./ui/AnimatedButton";
import { SearchableSelect } from "./ui/SearchableSelect";

export interface BusinessEdits {
  business_name: string;
  business_type: string;
  industry: string;
}

interface BusinessHeaderCardProps {
  client: Client;
  /** Save the edits; rejects with the server's refusal, shown on the form. */
  onSave: (edits: BusinessEdits) => Promise<void>;
}

interface EditFieldsProps {
  edits: BusinessEdits;
  saving: boolean;
  onChange: (edits: BusinessEdits) => void;
}

function EditFields({ edits, saving, onChange }: EditFieldsProps): ReactNode {
  return (
    <>
      <label htmlFor="edit-business-name">Business name</label>
      <input
        id="edit-business-name"
        type="text"
        value={edits.business_name}
        onChange={(e) => onChange({ ...edits, business_name: e.target.value })}
        disabled={saving}
        maxLength={200}
      />
      <label htmlFor="edit-business-industry">Industry</label>
      <SearchableSelect
        id="edit-business-industry"
        groups={INDUSTRY_GROUPS}
        value={edits.industry}
        onChange={(industry) => onChange({ ...edits, industry })}
        disabled={saving}
        placeholder="Search industries..."
      />
      <label htmlFor="edit-business-type">Entity type</label>
      <select
        id="edit-business-type"
        value={edits.business_type}
        onChange={(e) => onChange({ ...edits, business_type: e.target.value })}
        disabled={saving}
      >
        {ENTITY_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </>
  );
}

/** The business's name, industry and type, with the edit form in place. */
export function BusinessHeaderCard({ client, onSave }: BusinessHeaderCardProps): ReactNode {
  const [edits, setEdits] = useState<BusinessEdits | null>(null);
  const [editError, setEditError] = useState("");
  const [saving, setSaving] = useState(false);

  function startEditing(): void {
    setEdits({
      business_name: client.business_name,
      business_type: client.business_type,
      industry: client.industry,
    });
    setEditError("");
  }

  async function saveEdits(): Promise<void> {
    if (!edits) return;
    const trimmedName = edits.business_name.trim();
    if (!trimmedName) {
      setEditError("Business name is required");
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await onSave({ ...edits, business_name: trimmedName });
      setEdits(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      setSaving(false);
    }
  }

  if (edits) {
    return (
      <div className="business-header-card">
        <div className="business-edit-form">
          {editError && <ErrorMessage message={editError} onDismiss={() => setEditError("")} />}
          <EditFields edits={edits} saving={saving} onChange={setEdits} />
          <div className="business-edit-actions">
            <AnimatedButton
              type="button"
              variant="primary"
              disabled={saving}
              onClick={() => void saveEdits()}
            >
              {saving ? "Saving..." : "Save"}
            </AnimatedButton>
            <AnimatedButton type="button" disabled={saving} onClick={() => setEdits(null)}>
              Cancel
            </AnimatedButton>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="business-header-card">
      <div className="business-header-top">
        <h2>{client.business_name}</h2>
        <AnimatedButton
          type="button"
          className="edit-button"
          onClick={startEditing}
          aria-label="Edit business details"
        >
          Edit
        </AnimatedButton>
      </div>
      <div className="business-header-meta">
        <span className="industry-badge">{formatIndustry(client.industry)}</span>
        <span className="business-type">{formatBusinessType(client.business_type)}</span>
      </div>
    </div>
  );
}
